import { defineStore } from "pinia";
import { useGeneralStore } from "%/stores/general-store";
import SmartFeedEngine from "@/smart-feed/smart-feed-engine";
import {
  useMyPublicationCollaboratorsFeedStore,
} from "SmartFeed/stores/feed/use-my-publication-collaborators-feed-store";
import { useCreatorPublicationStore } from "%/stores/creator-publication-store";
import { validateEmail } from "@bloglovin/vue-component-library";
import {
  DEFAULT_INVITE_FORM_DATA,
  DEFAULT_INVITE_VALIDATION_ERRORS,
} from "@/constants/collaborator-constants";
import { useMyCollaboratorProposalFeedStore } from "@/smart-feed/stores/feed/my-collaborator-proposal-feed-store";
import { useMyCollaboratorInviteFeedStore } from "@/smart-feed/stores/feed/my-collaborator-invite-feed-store";
import {
  useMyPublicationCollaboratorsInviteeFeedStore,
} from "@/smart-feed/stores/feed/my-publication-collaborators-invitee-feed-store";
import { useActivePublicationFeedStore } from "SmartFeed/stores/feed/active-publication-feed-store";
import { useCollaboratingPublicationFeedStore } from "SmartFeed/stores/feed/collaborating-publication-feed-store";
import { usePublicationStore } from "@/stores/publication-store";
import {
  ROLE_CONTRIBUTOR,
  ROLE_MARKETER,
  ROLE_EDITOR,
  ROLE_PRODUCER,
  ROLE_DESIGNER,
  ROLE_ADMIN,
  ROLE_MODERATOR,
  ROLE_SUPPORT,
  COLLABORATOR_PAGE_STORIES,
  COLLABORATOR_PAGE_ADVERTISING,
  COLLABORATOR_PAGE_EVENTS,
  COLLABORATOR_PAGE_ANALYTICS,
  COLLABORATOR_PAGE_TEAM,
  COLLABORATOR_PAGE_CHAT_ROOM,
  COLLABORATOR_PAGE_SUPPORT,
  COLLABORATOR_PAGE_MY_USERS,
  COLLABORATOR_PAGE_REVENUE,
  COLLABORATOR_PAGE_SUBSCRIPTION_PLANS,
  COLLABORATOR_PAGE_COURSES,
  COLLABORATOR_PAGE_SETTINGS,
  COLLABORATOR_PAGE_BRANDING,
  COLLABORATOR_PAGE_PRO_FEATURES,
  COLLABORATOR_PAGE_DEPLOYMENT,
  COLLABORATOR_PAGE_RSS,
} from "@/constants/collaborator-constants";

export const useCollaboratorStore = defineStore("collaborator-store", {
  state: () => ({
    selectedCollaboratorForRoleChange: null,
    selectedInvitationReview: null,
    selectedProposalReview: null,
    collaboratorApplication: null,
    isLoadingInvitations: false,
    isLoadingApplications: false,
    isCreatingOnPlatformInvitation: false,
    isCreatingOffPlatformInvitation: false,
    isCreatingApplication: false,
    isAcceptingInvitation: false,
    isDenyingInvitation: false,
    isRevokingInvitation: false,
    isAcceptingApplication: false,
    isDenyingApplication: false,
    isReactivatingApplication: false,
    isRevokingApplication: false,
    isResendingInvitation: false,
    isEnablingCollaborator: false,
    isDisablingCollaborator: false,
    isInvitationReviewModalVisible: false,
    isProposalReviewModalVisible: false,
    isChangeRoleModalVisible: false,
    isCounterOfferModalVisible: false,
    isChangingRole: false,
    isLoadingPageOfApplicants: false,
    isLoadingApplication: false,
    error: null,
    isInviteModalVisible: false,
    inviteModalState: "invite",
    inviteFormData: { ...DEFAULT_INVITE_FORM_DATA },
    inviteValidationErrors: { ...DEFAULT_INVITE_VALIDATION_ERRORS },
    isSubmittingCounterOffer: false,
    role: "",
  }),
  getters: {
    orgId() {
      return useGeneralStore().orgId;
    },
    getInviteFormData() {
      return this.inviteFormData;
    },
    getInviteValidationErrors() {
      return this.inviteValidationErrors;
    },
    isOwner() {
      const isPublicationOwner = usePublicationStore().publication.org_id === this.orgId;
      const isOwner = this.role === "owner";

      return (
        isPublicationOwner ||
        isOwner
      );
    },
    isAdmin() {
      return this.role === ROLE_ADMIN;
    },
    isEditor() {
      return this.role === ROLE_EDITOR;
    },
    isContributor() {
      return this.role === ROLE_CONTRIBUTOR;
    },
    isDesigner() {
      return this.role === ROLE_DESIGNER;
    },
    isProducer() {
      return this.role === ROLE_PRODUCER;
    },
    isSupport() {
      return this.role === ROLE_SUPPORT;
    },
    isModerator() {
      return this.role === ROLE_MODERATOR;
    },
    isMarketer() {
      return this.role === ROLE_MARKETER;
    },
    canView() {
      const rolePageRules = [
        {
          role: ROLE_PRODUCER,
          include: [
            COLLABORATOR_PAGE_STORIES,
            COLLABORATOR_PAGE_ADVERTISING,
            COLLABORATOR_PAGE_ANALYTICS,
            COLLABORATOR_PAGE_TEAM,
            COLLABORATOR_PAGE_CHAT_ROOM,
            COLLABORATOR_PAGE_SUPPORT,
            COLLABORATOR_PAGE_MY_USERS,
          ],
        },
        {
          role: ROLE_EDITOR,
          exclude: [
            COLLABORATOR_PAGE_ADVERTISING,
            COLLABORATOR_PAGE_ANALYTICS,
            COLLABORATOR_PAGE_REVENUE,
            COLLABORATOR_PAGE_SUBSCRIPTION_PLANS,
            COLLABORATOR_PAGE_COURSES,
            COLLABORATOR_PAGE_SETTINGS,
            COLLABORATOR_PAGE_BRANDING,
            COLLABORATOR_PAGE_PRO_FEATURES,
            COLLABORATOR_PAGE_DEPLOYMENT,
            COLLABORATOR_PAGE_RSS,
          ],
        },
        {
          role: ROLE_DESIGNER,
          include: [
            COLLABORATOR_PAGE_STORIES,
          ],
        },
        {
          role: ROLE_CONTRIBUTOR,
          include: [
            COLLABORATOR_PAGE_STORIES,
          ],
        },
        {
          role: ROLE_MODERATOR,
          include: [
            COLLABORATOR_PAGE_CHAT_ROOM,
          ],
        },
        {
          role: ROLE_SUPPORT,
          include: [
            COLLABORATOR_PAGE_SUPPORT,
          ],
        },
        {
          role: ROLE_MARKETER,
          exclude: [
            COLLABORATOR_PAGE_STORIES,
            COLLABORATOR_PAGE_ADVERTISING,
            COLLABORATOR_PAGE_EVENTS,
            COLLABORATOR_PAGE_ANALYTICS,
            COLLABORATOR_PAGE_TEAM,
            COLLABORATOR_PAGE_CHAT_ROOM,
            COLLABORATOR_PAGE_SUPPORT,
            COLLABORATOR_PAGE_MY_USERS,
            COLLABORATOR_PAGE_REVENUE,
            COLLABORATOR_PAGE_SUBSCRIPTION_PLANS,
            COLLABORATOR_PAGE_COURSES,
            COLLABORATOR_PAGE_SETTINGS,
            COLLABORATOR_PAGE_BRANDING,
            COLLABORATOR_PAGE_PRO_FEATURES,
            COLLABORATOR_PAGE_DEPLOYMENT,
            COLLABORATOR_PAGE_RSS,
          ],
        },
        {
          role: ROLE_ADMIN,
          include: [],
        },
      ];

      const segments = window.location.pathname.split("/").filter(Boolean);
      let currentPage = "";

      if (segments.length > 0) {
        const last = segments[segments.length - 1];

        if (last.toLowerCase() === "pro" && segments.length > 1) {
          const prev = segments[segments.length - 2];
          currentPage = `${prev}/pro`;
        } else {
          currentPage = last;
        }
      }

      if (!currentPage) {
        return false;
      }

      if (this.isOwner) {
        return true;
      }

      const rule = rolePageRules.find(r => r.role === this.role) || null;

      if (!rule) {
        return false;
      }

      if (Array.isArray(rule.include) && rule.include.length > 0) {
        return rule.include.includes(currentPage);
      }

      if (Array.isArray(rule.exclude) && rule.exclude.length > 0) {
        return !rule.exclude.includes(currentPage);
      }

      return true;
    },
    canChangeOwner() {
      return (
        this.isOwner
      );
    },
    canApproveAdmin() {
      return (
        this.isOwner ||
        this.isAdmin
      );
    },
    canApproveCollaborators() {
      return (
        this.isOwner ||
        this.isAdmin ||
        this.isProducer
      );
    },
    canApproveUsers() {
      return (
        this.isOwner ||
        this.isAdmin ||
        this.isProducer
      );
    },
    canInviteCollaborators() {
      return (
        this.isOwner ||
        this.isAdmin ||
        this.isProducer ||
        this.isEditor
      );
    },
    canInviteUsers() {
      return (
        this.isOwner ||
        this.isAdmin ||
        this.isProducer ||
        this.isEditor
      );
    },
    canEditSettings() {
      return (
        this.isOwner ||
        this.isAdmin
      );
    },
    canEditStories() {
      return (
        this.isOwner ||
        this.isAdmin ||
        this.isProducer ||
        this.isEditor ||
        this.isDesigner ||
        this.isContributor
      );
    },
    canPublishStories() {
      return (
        this.isOwner ||
        this.isAdmin ||
        this.isProducer ||
        this.isEditor
      );
    },
    canRespondToSupportTickets() {
      return (
        this.isOwner ||
        this.isAdmin ||
        this.isProducer ||
        this.isEditor ||
        this.isSupport
      );
    },
    canEditPublication() {
      return (
        this.isOwner ||
        this.isAdmin ||
        this.isProducer ||
        this.isEditor ||
        this.isDesigner
      );
    },
    canPublishPublication() {
      return (
        this.isOwner ||
        this.isAdmin ||
        this.isProducer
      );
    },
    canManagePublication() {
      return (
        this.isOwner ||
        this.isAdmin
      );
    },
    canEditFeatures() {
      return (
        this.isOwner ||
        this.isAdmin ||
        this.isProducer
      );
    },
    canModerate() {
      return (
        this.isOwner ||
        this.isAdmin ||
        this.isProducer ||
        this.isEditor ||
        this.isModerator ||
        this.isMarketer
      );
    },
  },
  actions: {
    createOnPlatformInvitation(
      publicationId,
      orgId,
      ownerOrgId,
      name,
      role,
      onSuccessCallBack = null,
      onErrorCallback = null,
    ) {
      if (this.isCreatingOnPlatformInvitation) {
        return;
      }

      this.isCreatingOnPlatformInvitation = true;
      this.error = null;

      return this.apiPost(
        "/collaborator/invitation/on-platform",
        {
          publication_id: publicationId,
          org_id: orgId,
          owner_org_id: ownerOrgId,
          name,
          role,
        },
        () => {
          useMyPublicationCollaboratorsFeedStore().reload();
          if (onSuccessCallBack) {
            onSuccessCallBack();
          }
        },
        error => {
          if (onErrorCallback) {
            onErrorCallback();
          }
          this.error =
                error.response?.data?.message ||
                "Failed to create on-platform collaborator invitation";
        },
        () => {
          this.isCreatingOnPlatformInvitation = false;
        },
      );
    },
    createOffPlatformInvitation(
      publicationId,
      ownerOrgId,
      onSuccessCallBack = () => {
      },
      onErrorCallback = () => {
      },
    ) {
      if (this.isCreatingOffPlatformInvitation) {
        return;
      }

      this.isCreatingOffPlatformInvitation = true;
      this.error = null;

      const compensation = this.getCompensationFromFormData(this.inviteFormData);

      let frequency = "per_month";
      const flatFeeFrequency = this.getFrequencyValue(this.inviteFormData.flatFeeFrequency);
      const performanceBonusFrequency = this.getFrequencyValue(this.inviteFormData.performanceBonusFrequency);
      if (flatFeeFrequency) {
        frequency = flatFeeFrequency;
      } else if (performanceBonusFrequency) {
        frequency = performanceBonusFrequency;
      }

      const payload = {
        publication_id: publicationId,
        owner_org_id: ownerOrgId,
        email: this.inviteFormData.inviteeEmail,
        name: this.inviteFormData.inviteeName,
        role: this.inviteFormData.selectedRole.value,
        scope_of_work: this.inviteFormData.scopeOfWork,
        flat_fee_amount: compensation.flatFeeAmount,
        flat_fee_frequency: frequency,
        percent_revenue_value: compensation.percentageRevenueValue,
        performance_bonus_amount: compensation.performanceBonusAmount,
      };

      return this.apiPost(
        "/collaborator/invitation/off-platform",
        payload,
        () => {
          useMyPublicationCollaboratorsFeedStore().reload();
          useMyPublicationCollaboratorsInviteeFeedStore().reload();
          this.isInviteModalVisible = false;
          this.inviteFormData = { ...DEFAULT_INVITE_FORM_DATA };
          this.inviteValidationErrors = { ...DEFAULT_INVITE_VALIDATION_ERRORS };
          this.error = null;
          onSuccessCallBack();
        },
        error => {
          const prevErrors = error?.response?.data?.data?.previous;
          if (Array.isArray(prevErrors) && prevErrors.length > 0) {
            const prev = prevErrors[0];
            this.error = typeof prev === "string" ? prev : prev?.message || prev;
          } else {
            this.error =
                error?.response?.data?.message ||
                "Failed to create off-platform collaborator invitation and contract proposal";
          }
          onErrorCallback();
        },
        () => {
          this.isCreatingOffPlatformInvitation = false;
        },
      );
    },
    createApplication(
      publicationId,
      orgId,
      email,
      name,
      role,
      applicationMessage = "",
      successCallback = () => {
      },
    ) {
      if (this.isCreatingApplication) {
        return;
      }

      this.isCreatingApplication = true;
      this.error = null;

      return this.apiPost(
        "/collaborator/application",
        {
          publication_id: publicationId,
          org_id: orgId,
          email,
          name,
          role,
          application_message: applicationMessage,
        },
        response => {
          useCreatorPublicationStore().setCollaboratorRelationship(
            response.userRelationship,
          );
          successCallback();
        },
        error => {
          this.error =
                error.response?.data?.message ||
                "Failed to create collaborator application";
        },
        () => {
          this.isCreatingApplication = false;
        },
      );
    },
    acceptInvitation(publicationId, orgId, contractId, isInvite = false) {
      if (this.isAcceptingInvitation) {
        return;
      }
      this.isAcceptingInvitation = true;
      this.error = null;

      return this.apiPost(
        `/collaborator/${publicationId}/${orgId}/invitation/accept`,
        { is_invite: isInvite, contract_id: contractId },
        () => {
          if (isInvite) {
            useMyCollaboratorInviteFeedStore().reload();
          } else {
            useMyCollaboratorProposalFeedStore().reload();
          }
          useActivePublicationFeedStore().reload();
          useCollaboratingPublicationFeedStore().reload();
          this.closeProposalReviewModal();
        },
        error => {
          this.error =
                error.response?.data?.message ||
                "Failed to accept collaborator invitation";
        },
        () => {
          this.isAcceptingInvitation = false;
        },
      );
    },
    denyInvitation(publicationId, orgId, isInvite = false) {
      if (this.isDenyingInvitation) {
        return;
      }
      this.isDenyingInvitation = true;
      this.error = null;

      return this.apiPost(
        `/collaborator/${publicationId}/${orgId}/invitation/deny`,
        { is_invite: isInvite },
        () => {
          if (isInvite) {
            useMyCollaboratorInviteFeedStore().reload();
          } else {
            useMyCollaboratorProposalFeedStore().reload();
          }
          this.closeProposalReviewModal();
        },
        error => {
          this.error =
                error.response?.data?.message ||
                "Failed to deny collaborator invitation";
        },
        () => {
          this.isDenyingInvitation = false;
        },
      );
    },
    revokeInvitation(publicationId, orgId) {
      if (this.isRevokingInvitation) {
        return;
      }
      this.isRevokingInvitation = true;
      this.error = null;

      return this.apiPost(
        `/collaborator/${publicationId}/${orgId}/invitation/revoke`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.error =
                error.response?.data?.message ||
                "Failed to revoke collaborator invitation";
        },
        () => {
          this.isRevokingInvitation = false;
        },
      );
    },
    acceptApplication(publicationId, orgId) {
      if (this.isAcceptingApplication) {
        return;
      }
      this.isAcceptingApplication = true;
      this.error = null;

      return this.apiPost(
        `/collaborator/${publicationId}/${orgId}/application/accept`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.error =
                error.response?.data?.message ||
                "Failed to accept collaborator application";
        },
        () => {
          this.isAcceptingApplication = false;
        },
      );
    },
    denyApplication(publicationId, orgId) {
      if (this.isDenyingApplication) {
        return;
      }
      this.isDenyingApplication = true;
      this.error = null;

      return this.apiPost(
        `/collaborator/${publicationId}/${orgId}/application/deny`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.error =
                error.response?.data?.message ||
                "Failed to deny collaborator application";
        },
        () => {
          this.isDenyingApplication = false;
        },
      );
    },
    reactivateApplication(publicationId, orgId) {
      if (this.isReactivatingApplication) {
        return;
      }
      this.isReactivatingApplication = true;
      this.error = null;

      return this.apiPost(
        `/collaborator/${publicationId}/${orgId}/application/reactivate`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.error =
                error.response?.data?.message ||
                "Failed to reactivate collaborator application";
        },
        () => {
          this.isReactivatingApplication = false;
        },
      );
    },
    revokeApplication(publicationId, orgId) {
      if (this.isRevokingApplication) {
        return;
      }
      this.isRevokingApplication = true;
      this.error = null;

      return this.apiPost(
        `/collaborator/${publicationId}/${orgId}/application/revoke`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.error =
                error.response?.data?.message ||
                "Failed to revoke collaborator application";
        },
        () => {
          this.isRevokingApplication = false;
        },
      );
    },
    resendApplicantInvitation(publicationId, orgId) {
      if (this.isResendingInvitation) {
        return;
      }
      this.isResendingInvitation = true;
      this.error = null;

      return this.apiPost(
        `/collaborator/${publicationId}/${orgId}/invitation/resend`,
        {},
        response => {
          if (response && response.entities) {
            new SmartFeedEngine().addEntities(response.entities);
          }
        },
        error => {
          this.error =
                error.response?.data?.message ||
                "Failed to resend collaborator invitation";
        },
        () => {
          this.isResendingInvitation = false;
        },
      );
    },
    enableCollaborator(publicationId, orgId) {
      if (this.isEnablingCollaborator) {
        return;
      }
      this.isEnablingCollaborator = true;
      this.error = null;

      return this.apiPost(
        `/collaborator/${publicationId}/${orgId}/enable`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.error =
                error.response?.data?.message || "Failed to enable collaborator";
        },
        () => {
          this.isEnablingCollaborator = false;
        },
      );
    },
    disableCollaborator(publicationId, orgId) {
      if (this.isDisablingCollaborator) {
        return;
      }
      this.isDisablingCollaborator = true;
      this.error = null;

      return this.apiPost(
        `/collaborator/${publicationId}/${orgId}/disable`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.error =
                error.response?.data?.message || "Failed to disable collaborator";
        },
        () => {
          this.isDisablingCollaborator = false;
        },
      );
    },
    changeRole(applicantId, publicationId, role) {
      if (this.isChangingRole) {
        return;
      }
      this.isChangingRole = true;
      this.error = null;

      return this.apiPost(
        `/collaborator/${applicantId}/${publicationId}/role`,
        { role },
        response => {
          new SmartFeedEngine().addEntities(response.entities);
          this.closeChangeRoleModal();
        },
        error => {
          this.error =
                error.response?.data?.message ||
                "Failed to change collaborator role";
        },
        () => {
          this.isChangingRole = false;
        },
      );
    },
    getApplication({ publicationId, orgId }) {
      if (this.isLoadingApplication) {
        return;
      }
      this.isLoadingApplication = true;
      this.error = null;

      return this.apiGet(
        `/collaborator/${publicationId}/${orgId}/application`,
        {},
        data => {
          this.collaboratorApplication = data;
        },
        error => {
          this.error =
                error.response?.data?.message ||
                "Failed to load collaborator application";
        },
        () => {
          this.isLoadingApplication = false;
        },
      );
    },
    openInvitationReviewModal(invitation) {
      this.selectedInvitationReview = invitation;
      this.isInvitationReviewModalVisible = true;
    },
    closeInvitationReviewModal() {
      this.isInvitationReviewModalVisible = false;
      this.selectedInvitationReview = null;
    },
    openProposalReviewModal(proposal) {
      this.selectedProposalReview = proposal;
      this.isProposalReviewModalVisible = true;
    },
    closeProposalReviewModal() {
      this.isProposalReviewModalVisible = false;
      this.selectedProposalReview = null;
    },
    openCounterOfferModal() {
      this.isCounterOfferModalVisible = true;
    },
    closeCounterOfferModal() {
      this.isCounterOfferModalVisible = false;
      this.isProposalReviewModalVisible = true;
    },
    openChangeRoleModal(entity) {
      this.error = null;
      this.isChangeRoleModalVisible = true;
      this.selectedCollaboratorForRoleChange = entity;
    },
    closeChangeRoleModal() {
      this.isChangeRoleModalVisible = false;
      this.selectedCollaboratorForRoleChange = null;
      this.error = null;
    },
    openInviteModal() {
      this.error = null;
      this.inviteFormData = { ...DEFAULT_INVITE_FORM_DATA };
      this.inviteValidationErrors = { ...DEFAULT_INVITE_VALIDATION_ERRORS };
      this.inviteModalState = "invite";
      this.isInviteModalVisible = true;
    },
    closeInviteModal() {
      if (this.inviteModalState === "proposal") {
        this.inviteModalState = "invite";
        this.inviteValidationErrors = { ...DEFAULT_INVITE_VALIDATION_ERRORS };
        this.error = null;
      } else {
        this.isInviteModalVisible = false;
        this.inviteFormData = { ...DEFAULT_INVITE_FORM_DATA };
        this.inviteValidationErrors = { ...DEFAULT_INVITE_VALIDATION_ERRORS };
        this.error = null;
      }
    },
    setInviteModalState(newState) {
      this.inviteModalState = newState;
      this.inviteValidationErrors = { ...DEFAULT_INVITE_VALIDATION_ERRORS };
    },
    updateInviteFormData(newData) {
      this.inviteFormData = { ...this.inviteFormData, ...newData };
      if (newData.inviteeName) {
        this.inviteValidationErrors.inviteeName = "";
      }
      if (newData.inviteeEmail) {
        this.inviteValidationErrors.inviteeEmail = "";
      }
      if (newData.selectedRole?.value) {
        this.inviteValidationErrors.role = "";
      }
      if (newData.scopeOfWork) {
        this.inviteValidationErrors.scopeOfWork = "";
      }
      if (
        newData.flatFee !== undefined ||
          newData.percentRevenue !== undefined ||
          newData.performanceBonus !== undefined
      ) {
        if (
          this.inviteFormData.flatFee ||
            this.inviteFormData.percentRevenue ||
            this.inviteFormData.performanceBonus
        ) {
          this.inviteValidationErrors.paymentType = "";
        }
      }
      if (newData.flatFeeAmount || newData.flatFeeFrequency) {
        if (
          this.inviteFormData.flatFeeAmount &&
            this.inviteFormData.flatFeeFrequency
        ) {
          this.inviteValidationErrors.flatFeeDetails = "";
        }
      }
      if (newData.percentRevenueValue) {
        this.inviteValidationErrors.percentRevenueValue = "";
      }
      if (newData.performanceBonusAmount || newData.performanceBonusFrequency) {
        if (
          this.inviteFormData.performanceBonusAmount &&
            this.inviteFormData.performanceBonusFrequency
        ) {
          this.inviteValidationErrors.performanceBonusDetails = "";
        }
      }
    },
    validateInviteForm() {
      this.inviteValidationErrors = { ...DEFAULT_INVITE_VALIDATION_ERRORS };
      let isValid = true;

      if (!this.inviteFormData.selectedRole?.value) {
        this.inviteValidationErrors.role = "Please select a role.";
        isValid = false;
      }
      if (!this.inviteFormData.inviteeEmail) {
        this.inviteValidationErrors.inviteeEmail = "Email is required";
        isValid = false;
      } else if (!validateEmail(this.inviteFormData.inviteeEmail)) {
        this.inviteValidationErrors.inviteeEmail =
            "Please enter a valid email address";
        isValid = false;
      }
      if (!this.inviteFormData.inviteeName) {
        this.inviteValidationErrors.inviteeName = "Invitee name is required";
        isValid = false;
      }
      return isValid;
    },
    validateProposalForm() {
      this.inviteValidationErrors.flatFeeDetails = "";
      this.inviteValidationErrors.percentRevenueValue = "";
      this.inviteValidationErrors.performanceBonusDetails = "";
      this.inviteValidationErrors.scopeOfWork = "";
      this.inviteValidationErrors.paymentType = "";
      let isValid = true;
      const compensation = this.getCompensationFromFormData(this.inviteFormData);

      if (!this.inviteFormData.scopeOfWork) {
        this.inviteValidationErrors.scopeOfWork = "Scope of work is required";
        isValid = false;
      }

      if (!this.inviteFormData.noCompensation && !compensation.hasCompensation) {
        this.inviteValidationErrors.performanceBonusDetails =
            "At least one compensation type is required";
        isValid = false;
      }

      if (this.inviteFormData.flatFee && compensation.flatFeeAmount <= 0) {
        this.inviteValidationErrors.flatFeeDetails =
            "Amount is required for Flat Fee.";
        isValid = false;
      }

      if (this.inviteFormData.percentRevenue && !this.inviteFormData.percentRevenueValue) {
        this.inviteValidationErrors.percentRevenueValue =
            "Percentage value is required for Percent of Revenue.";
        isValid = false;
      }

      if (this.inviteFormData.percentRevenue && compensation.percentageRevenueValue <= 0) {
        this.inviteValidationErrors.percentRevenueValue =
            "Percentage must be a positive number.";
        isValid = false;
      }
      return isValid;
    },
    createCounterOffer(proposal, formData, isInvite = false) {
      if (this.isSubmittingCounterOffer) {
        return;
      }

      this.isSubmittingCounterOffer = true;
      this.error = null;

      if (!proposal || !proposal.contract.contract_id) {
        this.error = "Invalid proposal selected";
        this.isSubmittingCounterOffer = false;
        return;
      }

      const compensation = this.getCompensationFromFormData(formData);

      let frequency = "per_month";
      const flatFeeFrequency = this.getFrequencyValue(formData.flatFeeFrequency);
      const performanceBonusFrequency = this.getFrequencyValue(formData.performanceBonusFrequency);
      if (formData.flatFee && flatFeeFrequency) {
        frequency = flatFeeFrequency;
      } else if (formData.performanceBonus && performanceBonusFrequency) {
        frequency = performanceBonusFrequency;
      }

      const payload = {
        org_id: this.orgId,
        content: {
          scope_of_work: formData.scopeOfWork,
          frequency,
          flat_fee: compensation.flatFeeAmount,
          percentage_revenue: compensation.percentageRevenueValue,
          performance_based_revenue: compensation.performanceBonusAmount,
        },
      };

      return this.apiPost(
        `/contract/${proposal.contract.contract_id}/counter`,
        payload,
        () => {
          if (isInvite) {
            useMyCollaboratorInviteFeedStore().reload();
          } else {
            useMyCollaboratorProposalFeedStore().reload();
          }
          this.closeCounterOfferModal();
        },
        error => {
          this.error =
                error.response?.data?.message || "Failed to submit counter offer";
        },
        () => {
          this.isSubmittingCounterOffer = false;
        },
      );
    },
    getFrequencyValue(frequencyData) {
      if (typeof frequencyData === "string" && frequencyData) {
        return frequencyData;
      }

      if (frequencyData && frequencyData.value) {
        return frequencyData.value;
      }

      return "";
    },
    getCompensationFromFormData(formData) {
      const parsedFlatFeeAmount = parseFloat(formData.flatFeeAmount);
      const parsedPercentageRevenue = parseFloat(formData.percentRevenueValue);
      const parsedPerformanceBonus = parseFloat(formData.performanceBonusAmount);

      const hasFlatFeeAmount = Number.isFinite(parsedFlatFeeAmount) && parsedFlatFeeAmount > 0;
      const hasPercentageRevenue = Number.isFinite(parsedPercentageRevenue) && parsedPercentageRevenue > 0;
      const hasPerformanceBonusAmount = Number.isFinite(parsedPerformanceBonus) && parsedPerformanceBonus > 0;

      const hasFlatFee = !!formData.flatFee || hasFlatFeeAmount;
      const hasPerformanceBonus = !!formData.performanceBonus || hasPerformanceBonusAmount;
      const hasNoCompensation = !!formData.noCompensation;
      const hasCompensation = !hasNoCompensation && (hasFlatFee || hasPercentageRevenue || hasPerformanceBonus);

      let flatFeeAmount = 0.0;
      if (!hasNoCompensation && hasFlatFee && hasFlatFeeAmount) {
        flatFeeAmount = parsedFlatFeeAmount;
      }

      let percentageRevenueValue = 0.0;
      if (!hasNoCompensation && hasPercentageRevenue) {
        percentageRevenueValue = parsedPercentageRevenue;
      }

      let performanceBonusAmount = 0.0;
      if (!hasNoCompensation && hasPerformanceBonus) {
        if (hasPerformanceBonusAmount) {
          performanceBonusAmount = parsedPerformanceBonus;
        } else {
          performanceBonusAmount = 1;
        }
      }

      return {
        hasCompensation,
        flatFeeAmount,
        percentageRevenueValue,
        performanceBonusAmount,
      };
    },
    setRole(newRole) {
      this.role = newRole;
    },
  },
});
